export const namespaced = true;
import StudentService from '@/services/StudentService.js';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import keyBy from 'lodash/keyBy';
import moment from 'moment-timezone';
import {
  basicJapaneseYearMonth,
  basicPresent,
  basicSubFieldPresent,
  basicNextTeachingMaterialMonth,
  basicTeachingMaterialShapeDisplay,
  basicTeachingMaterialAlgebraDisplay,
  basicTeachingMaterialJapaneseDisplay,
  basicTeachingMaterialKidsMathematicsDisplay,
  basicTeachingMaterialAdvancedMathematicsDisplay,
  basicDate,
  preRegistrationStatusDisplay,
  basicTrialStudentNameIdPresent,
  preRegistrationDateStatusDisplay,
} from '@/presenters/fields';
import { reformatDatetimeToMonthJP } from '@/utils/year_helpers';

export const state = {
  students: [],
  trialStudents: [],
  closedOrPreRegistrationStudents: [],
  allStudentCount: 0,
  allTrialStudentCount: 0,
  allClosedOrPreRegistrationStudentCount: 0,
  student: {},
  editingStudent: null,
  selectedStudents: [],
  studentAffiliation: null,
};

export const mutations = {
  SET_STUDENTS(state, students) {
    state.students = formatStudents(students);
  },
  SET_TRIAL_STUDENTS(state, students) {
    state.trialStudents = formatStudents(students);
  },
  SET_CLOSED_OR_PRE_REGISTRATION_STUDENTS(state, students) {
    state.closedOrPreRegistrationStudents = formatStudents(students);
  },
  SET_ALL_STUDENT_COUNT(state, count) {
    state.allStudentCount = count;
  },
  SET_ALL_TRIAL_STUDENT_COUNT(state, count) {
    state.allTrialStudentCount = count;
  },
  SET_ALL_CLOSED_AND_PRE_STUDENT_COUNT(state, count) {
    state.allClosedOrPreRegistrationStudentCount = count;
  },
  SET_STUDENT(state, student) {
    formatStudent(student);
    state.student = student;
  },
  RESTORE_FORM_DATA(state, student) {
    state.student = student;
  },
  SET_EDITING_STUDENT(state, student) {
    state.editingStudent = student;
  },
  DELETE_EDITING_STUDENT(state) {
    state.editingStudent = null;
  },
  UPDATE_STUDENT(state, student) {
    state.student = student;
  },
  ADD_STUDENT(state, student) {
    state.student = student;
  },
  CHANGE_STUDENT_PASSWORD() {
    // We don't store Password inside Vuex, so we don't need to do anything here
  },
  SET_SELECTED_STUDENTS(state, students) {
    state.selectedStudents = students;
  },
  DELETE_SELECTED_STUDENTS(state) {
    state.selectedStudents = [];
  },
  SET_STUDENT_AFFILIATION(state, studentAffiliation) {
    state.studentAffiliation = studentAffiliation;
  },
  DELETE_STUDENT_AFFILIATION(state) {
    state.studentAffiliation = null;
  },
  DEACTIVATE_STUDENTS(state, students) {
    students.forEach((updatedStudent) => {
      let student = state.students.find((item) => item.id === updatedStudent.id);
      student.deactivatedDatetime = updatedStudent.deactivatedDatetime;
    });
  },
  REACTIVATE_STUDENTS(state, students) {
    students.forEach((updatedStudent) => {
      let student = state.students.find((item) => item.id === updatedStudent.id);
      student.deactivatedDatetime = updatedStudent.deactivatedDatetime;
    });
  },
  UPDATE_LEVEL_STUDENTS(state, students) {
    students.forEach((updatedStudent) => {
      let student = state.students.find((item) => item.id === updatedStudent.id);
      // Search from deactivated student tab if not in the normal student tab
      if (!student) student = state.closedOrPreRegistrationStudents.find((item) => item.id === updatedStudent.id);

      student.levels = updatedStudent.levels;
      student.currentLevelMaterials = updatedStudent.currentLevelMaterials;
    });
  },
  UPGRADE_FIGURED_TEACHING_MATERIAL(state, students) {
    students.forEach((updatedStudent) => {
      formatStudent(updatedStudent);
      let student = state.closedOrPreRegistrationStudents.find((item) => item.id === updatedStudent.id);
      student.previousFiguredTeachingMaterials = student.figuredTeachingMaterials;
      student.previousLevelMaterials = student.currentLevelMaterials;
      student.figuredTeachingMaterials = updatedStudent.figuredTeachingMaterials;
      student.figuredTeachingMaterialCodes = updatedStudent.figuredTeachingMaterialCodes;
      student.currentLevelMaterials = updatedStudent.currentLevelMaterials;
    });
  },
  UPGRADE_NEXT_FIGURED_TEACHING_MATERIAL(state, students) {
    students.forEach((updatedStudent) => {
      formatStudent(updatedStudent);
      let student = state.students.find((item) => item.id === updatedStudent.id);
      student.nextFiguredTeachingMaterials = updatedStudent.nextFiguredTeachingMaterials;
      student.nextFiguredTeachingMaterialCodes = updatedStudent.nextFiguredTeachingMaterialCodes;
      student.nextLevelMaterials = updatedStudent.nextLevelMaterials;
    });
  },
  UPGRADE_IMMEDIATE_NEXT_FIGURED_TEACHING_MATERIAL(state, students) {
    students.forEach((updatedStudent) => {
      formatStudent(updatedStudent);
      let student = state.students.find((item) => item.id === updatedStudent.id);
      student.previousFiguredTeachingMaterials = student.figuredTeachingMaterials;
      student.previousLevelMaterials = student.currentLevelMaterials;
      student.figuredTeachingMaterials = updatedStudent.figuredTeachingMaterials;
      student.figuredTeachingMaterialCodes = updatedStudent.figuredTeachingMaterialCodes;
      student.currentLevelMaterials = updatedStudent.currentLevelMaterials;
    });
  },
  CLEAR_NEXT_FIGURED_TEACHING_MATERIAL(state, students) {
    students.forEach((updatedStudent) => {
      let student = state.students.find((item) => item.id === updatedStudent.id);
      student.nextFiguredTeachingMaterials = [];
      student.nextFiguredTeachingMaterialCodes = null;
      student.nextLevelMaterials = [];
    });
  },
  CHANGE_PRE_REGISTRATION_TIME(state, students) {
    students.forEach((updatedStudent) => {
      let student = state.closedOrPreRegistrationStudents.find((item) => item.id === updatedStudent.id);
      if (updatedStudent.reactivatedDatetime) {
        student.reactivatedDatetime = updatedStudent.reactivatedDatetime;
      }
      if (updatedStudent.preRegistrationDatetime) {
        student.preRegistrationDatetime = updatedStudent.preRegistrationDatetime;
      }
      if (updatedStudent.preRegistration) {
        student.preRegistration = updatedStudent.preRegistration;
      }
      if (updatedStudent.deactivated) {
        student.deactivated = updatedStudent.deactivated;
      }
    });
  },
  CLEAR_PRE_REGISTRATION_TIME(state, students) {
    students.forEach((updatedStudent) => {
      let student = state.closedOrPreRegistrationStudents.find((item) => item.id === updatedStudent.id);
      if (updatedStudent.reactivatedDatetime == null) {
        student.reactivatedDatetime = updatedStudent.reactivatedDatetime;
      }
      if (updatedStudent.preRegistrationDatetime == null) {
        student.preRegistrationDatetime = updatedStudent.preRegistrationDatetime;
      }
    });
  },
};

export const actions = {
  fetchStudents({ commit }, payload) {
    return StudentService.all(payload).then((response) => {
      commit('SET_STUDENTS', response.data.rows);
      commit('SET_ALL_STUDENT_COUNT', response.data.count);
    });
  },
  fetchTrialStudents({ commit }, payload) {
    return StudentService.all(payload).then((response) => {
      commit('SET_TRIAL_STUDENTS', response.data.rows);
      commit('SET_ALL_TRIAL_STUDENT_COUNT', response.data.count);
    });
  },
  fetchClosedOrPreRegistrationStudents({ commit }, payload) {
    return StudentService.all(payload).then((response) => {
      let students = map(response.data.rows, (student) => {
        if (student.preRegistrationDatetime) {
          student.preRegistrationMonth = reformatDatetimeToMonthJP(student.preRegistrationDatetime);
        }
        return student;
      });
      commit('SET_CLOSED_OR_PRE_REGISTRATION_STUDENTS', students);
      commit('SET_ALL_CLOSED_AND_PRE_STUDENT_COUNT', response.data.count);
    });
  },
  fetchStudent({ commit }, id) {
    // Note: always fetch from API, because Student object from the local list won't have
    // enough data to show (example: gender, address, schoolYear...)
    return StudentService.get(id).then((response) => {
      let student = response.data;
      if (student.preRegistrationDatetime) {
        student.preRegistrationMonth = reformatDatetimeToMonthJP(student.preRegistrationDatetime);
      }
      commit('SET_STUDENT', student);
      return response.data;
    });
  },
  restoreFormData({ commit }, formData) {
    commit('RESTORE_FORM_DATA', formData);
  },
  startEditingStudent({ commit }, student) {
    commit('SET_EDITING_STUDENT', student);
  },
  finishEditingStudent({ commit, state }, id) {
    if (state.editingStudent) {
      let formattedStudent = cloneDeep(state.editingStudent);
      if (formattedStudent.corporation) {
        formattedStudent = omit(formattedStudent, ['corporation']);
      }
      if (formattedStudent.brand) {
        formattedStudent = omit(formattedStudent, ['brand']);
      }
      if (formattedStudent.school) {
        formattedStudent = omit(formattedStudent, ['school']);
      }
      if (formattedStudent.course) {
        formattedStudent = omit(formattedStudent, ['course']);
      }
      if (formattedStudent.tamaiClass) {
        formattedStudent.tamaiClassId = formattedStudent.tamaiClass.id;
        formattedStudent = omit(formattedStudent, ['tamaiClass']);
      }
      if (formattedStudent.isImmediately) {
        formattedStudent.immediateNextFiguredTeachingMaterialCodes = formattedStudent.nextFiguredTeachingMaterialCodes;
      }
      if (!formattedStudent.nextFiguredTeachingMaterialCodes || formattedStudent.isImmediately) {
        formattedStudent = omit(formattedStudent, ['nextFiguredTeachingMaterialCodes']);
      }
      if (formattedStudent.gender) {
        formattedStudent.genderId = formattedStudent.gender.id;
        formattedStudent = omit(formattedStudent, ['gender']);
      }
      if (formattedStudent.schoolYear) {
        formattedStudent.schoolYearId = formattedStudent.schoolYear.id;
        formattedStudent = omit(formattedStudent, ['schoolYear']);
      }
      if (formattedStudent.citizenship) {
        formattedStudent.countryOfCitizenshipId = formattedStudent.citizenship.id;
        formattedStudent = omit(formattedStudent, ['citizenship']);
      }
      if (formattedStudent.residence) {
        formattedStudent.countryOfResidenceId = formattedStudent.residence.id;
        formattedStudent = omit(formattedStudent, ['residence']);
      }
      if (formattedStudent.activationId) {
        // This is for creating new Student flow
        switch (formattedStudent.activationId) {
          case '2':
            formattedStudent.preRegistration = true;
            break;
          case '3':
            formattedStudent.preRegistration = true;
            if (formattedStudent.preRegistrationMonth) {
              formattedStudent.preRegistrationDatetime = formattedStudent.preRegistrationMonth.value;
            }
            break;
          case '1':
          default:
            formattedStudent = omit(formattedStudent, ['preRegistration']);
            break;
        }
        formattedStudent = omit(formattedStudent, ['activationId', 'preRegistrationMonth']);
      } else {
        // And this is for editing Student flow
        if (formattedStudent.deactivateMonth) {
          formattedStudent.deactivatedDatetime = formattedStudent.deactivateMonth.value;
          formattedStudent = omit(formattedStudent, ['deactivateMonth']);
        }
        formattedStudent = omit(formattedStudent, [
          'currentLevelMaterials',
          'nextLevelMaterials',
          'figuredTeachingMaterials',
          'nextFiguredTeachingMaterials',
          'levels',
          'mainTeacher',
          'figuredTeachingMaterialCodes', // We won't allow to edit this here
        ]);
      }
      if (id) {
        return StudentService.update(id, formattedStudent).then((response) => {
          commit('DELETE_EDITING_STUDENT');
          commit('UPDATE_STUDENT', response.data);
        });
      } else {
        return StudentService.create(formattedStudent).then((response) => {
          commit('DELETE_EDITING_STUDENT');
          commit('ADD_STUDENT', response.data);
        });
      }
    }
  },
  clearEditingStudent({ commit, state }) {
    if (state.editingStudent) {
      commit('DELETE_EDITING_STUDENT');
    }
  },
  changeStudentPassword({ commit }, { id, password }) {
    return StudentService.changePassword(id, password).then(() => {
      commit('CHANGE_STUDENT_PASSWORD');
    });
  },
  storeSelectedStudents({ commit }, students) {
    commit('SET_SELECTED_STUDENTS', students);
  },
  cleanSelectedStudents({ commit }) {
    commit('DELETE_SELECTED_STUDENTS');
  },
  startEditingStudentAffiliation({ commit }, studentAffiliation) {
    commit('SET_STUDENT_AFFILIATION', studentAffiliation);
  },
  clearEditingStudentAffiliation({ commit, state }) {
    if (state.studentAffiliation) {
      commit('DELETE_STUDENT_AFFILIATION');
    }
  },
  finishEditingStudentAffiliation({ state }) {
    if (state.studentAffiliation && state.studentAffiliation.course && state.studentAffiliation.tamaiClass) {
      let studentIds = map(state.selectedStudents, (student) => {
        return student.id;
      });
      const payload = {
        studentIds,
        tamaiClassId: state.studentAffiliation.tamaiClass.id,
      };
      return StudentService.changeAffiliation(payload).then((response) => {
        return response.data;
      });
    }
  },
  deactivateStudents({ commit, state }, dateTime) {
    let studentIds = map(state.selectedStudents, (student) => {
      return student.id;
    });
    const payload = {
      studentIds,
      deactivatedDatetime: dateTime,
    };
    return StudentService.deactivate(payload).then((response) => {
      commit('DEACTIVATE_STUDENTS', response.data);
    });
  },
  reactivateStudents({ commit, state }) {
    let studentIds = map(state.selectedStudents, (student) => {
      return student.id;
    });
    return StudentService.reactivate({ studentIds }).then((response) => {
      commit('REACTIVATE_STUDENTS', response.data);
    });
  },
  upgradeLevelStudents({ commit, state }, type) {
    let studentDetails = map(state.selectedStudents, (student) => {
      let currentLevelMaterials = keyBy(student.currentLevelMaterials, 'level');
      let currentBookName = currentLevelMaterials[type].name;
      return { id: student.id, bookName: currentBookName };
    });
    const payload = {
      studentDetails,
      type,
    };
    return StudentService.upgradeLevel(payload).then((response) => {
      commit('UPDATE_LEVEL_STUDENTS', response.data);
    });
  },
  downgradeLevelStudents({ commit, state }, type) {
    const studentDetails = map(state.selectedStudents, (student) => {
      const currentLevelMaterials = keyBy(student.currentLevelMaterials, 'level');
      let currentBookName;
      if (!isEmpty(currentLevelMaterials[type])) {
        currentBookName = currentLevelMaterials[type].name;
      } else if (type == 'level_3.1' || type == 'level_3.2') {
        // 中学生の図形の極(G3平面G3空間)から平面もしくは空間の進級取消する場合、
        // currentLevelMaterialsにはlevel_3のみ存在するので、そのnameを取得する
        currentBookName = currentLevelMaterials['level_3'].name;
      }
      return { id: student.id, bookName: currentBookName };
    });
    const payload = {
      studentDetails,
      type,
    };
    return StudentService.downgradeLevel(payload).then((response) => {
      commit('UPDATE_LEVEL_STUDENTS', response.data);
    });
  },
  upgradeFiguredTeachingMaterial({ commit, state }, figuredTeachingMaterialCodes) {
    let studentIds = map(state.selectedStudents, (student) => {
      return student.id;
    });
    const payload = {
      studentIds,
      figuredTeachingMaterialCodes,
    };
    return StudentService.upgradeFiguredTeachingMaterial(payload).then((response) => {
      commit('UPGRADE_FIGURED_TEACHING_MATERIAL', response.data);
    });
  },
  upgradeNextFiguredTeachingMaterial({ commit, state }, nextFiguredTeachingMaterialCodes) {
    let studentIds = map(state.selectedStudents, (student) => {
      return student.id;
    });
    const payload = {
      studentIds,
      nextFiguredTeachingMaterialCodes,
    };
    return StudentService.upgradeNextFiguredTeachingMaterial(payload).then((response) => {
      commit('UPGRADE_NEXT_FIGURED_TEACHING_MATERIAL', response.data);
    });
  },
  upgradeImmediateNextFiguredTeachingMaterial({ commit, state }, immediateNextFiguredTeachingMaterialCodes) {
    let studentIds = map(state.selectedStudents, (student) => {
      return student.id;
    });
    const payload = {
      studentIds,
      immediateNextFiguredTeachingMaterialCodes,
    };
    return StudentService.upgradeNextFiguredTeachingMaterial(payload).then((response) => {
      commit('UPGRADE_IMMEDIATE_NEXT_FIGURED_TEACHING_MATERIAL', response.data);
      commit('CLEAR_NEXT_FIGURED_TEACHING_MATERIAL', response.data);
    });
  },
  clearNextFiguredTeachingMaterial({ commit, state }) {
    let studentIds = map(state.selectedStudents, (student) => {
      return student.id;
    });
    return StudentService.clearNextFiguredTeachingMaterial({ studentIds }).then((response) => {
      commit('CLEAR_NEXT_FIGURED_TEACHING_MATERIAL', response.data);
    });
  },
  changePreRegistrationTime({ commit, state }, expectedMonth) {
    let studentIds = map(state.selectedStudents, (student) => {
      return student.id;
    });
    const payload = {
      studentIds,
    };
    if (expectedMonth == 'now') {
      payload['type'] = 'force';
    } else {
      payload['updateDatetime'] = expectedMonth;
    }
    return StudentService.changePreRegistrationTime(payload).then((response) => {
      commit('CHANGE_PRE_REGISTRATION_TIME', response.data);
    });
  },
  clearPreRegistrationTime({ commit, state }) {
    let studentIds = map(state.selectedStudents, (student) => {
      return student.id;
    });
    return StudentService.clearPreRegistrationTime({ studentIds }).then((response) => {
      commit('CLEAR_PRE_REGISTRATION_TIME', response.data);
    });
  },
  resetPasswordAndExportPdf({ state }) {
    const students = map(state.selectedStudents, (student) => {
      // materialName
      let materialNames = [];
      materialNames.push(basicTeachingMaterialShapeDisplay(student.currentLevelMaterials));
      materialNames.push(basicTeachingMaterialAlgebraDisplay(student.currentLevelMaterials));
      materialNames.push(basicTeachingMaterialJapaneseDisplay(student.currentLevelMaterials));
      materialNames.push(basicTeachingMaterialKidsMathematicsDisplay(student.currentLevelMaterials));
      materialNames.push(basicTeachingMaterialAdvancedMathematicsDisplay(student.currentLevelMaterials));
      materialNames = materialNames.filter((name) => name != '―');
      const materialName = basicPresent(materialNames.join('、'));
      // trialExpirationDatetime
      const trialExpirationDatetime = student.trialExpirationDatetime
        ? basicDate(student.trialExpirationDatetime)
        : null;

      return {
        id: student.id,
        uid: student.uid,
        name: student.name,
        materialName: materialName,
        trialExpirationDatetime: trialExpirationDatetime,
      };
    });

    return StudentService.resetPasswordAndExportPdf({ students }).then((response) => {
      const blob = new Blob([response.data]);
      const fileName = `students-${moment().format('X')}`; // format('X') is Unix Timestamp

      // Create a tag and trigger click to download url
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${fileName}.pdf`;
      link.click();
    });
  },
};

export const getters = {
  getStudentById: (state) => (id) => {
    return state.students.find((item) => item.id == id);
  },
  getTrialStudentById: (state) => (id) => {
    return state.trialStudents.find((item) => item.id == id);
  },
  getClosedOrPreRegistrationStudentById: (state) => (id) => {
    return state.closedOrPreRegistrationStudents.find((item) => item.id == id);
  },
  totalStudents: (state) => {
    return state.allStudentCount;
  },
  totalTrialStudents: (state) => {
    return state.allTrialStudentCount;
  },
  totalClosedOrPreRegistrationStudents: (state) => {
    return state.allClosedOrPreRegistrationStudentCount;
  },
  isStudentActive: (state) => {
    if (
      state.student.reactivatedDatetime ||
      state.student.preRegistrationDatetime ||
      state.student.deactivated ||
      state.student.preRegistration
    ) {
      return false;
    } else {
      return true;
    }
  },
  getExcelFormattedStudents: () => (students, fields) => {
    return map(students, (student) => {
      let data = {
        法人名: basicSubFieldPresent(student, 'corporation', 'name'),
        ブランド名: basicSubFieldPresent(student, 'brand', 'name'),
        教場名: basicSubFieldPresent(student, 'school', 'name'),
        コース名: basicSubFieldPresent(student, 'course', 'name'),
        クラス名: basicSubFieldPresent(student, 'tamaiClass', 'name'),
        '先生名（担当・メイン）': basicSubFieldPresent(student, 'mainTeacher', 'name'),
        生徒ID: basicPresent(student.uid),
        生徒名: basicPresent(student.name),
        生徒名フリガナ: basicPresent(student.nameKana),
        '教材（極 - 図形）': basicTeachingMaterialShapeDisplay(student.currentLevelMaterials),
        '教材（極 - 数）': basicTeachingMaterialAlgebraDisplay(student.currentLevelMaterials),
        '教材（国語）': basicTeachingMaterialJapaneseDisplay(student.currentLevelMaterials),
        '教材（遊）': basicTeachingMaterialKidsMathematicsDisplay(student.currentLevelMaterials),
        '教材（悟）': basicTeachingMaterialAdvancedMathematicsDisplay(student.currentLevelMaterials),
        '教材変更 – 変更適用月': basicNextTeachingMaterialMonth(student.nextFiguredTeachingMaterials),
        '教材変更 – 変更後教材（極 - 図形）': basicTeachingMaterialShapeDisplay(student.nextLevelMaterials),
        '教材変更 – 変更後教材（極 - 数）': basicTeachingMaterialAlgebraDisplay(student.nextLevelMaterials),
        '教材変更 – 変更後教材（国語）': basicTeachingMaterialJapaneseDisplay(student.nextLevelMaterials),
        '教材変更 – 変更後教材（遊）': basicTeachingMaterialKidsMathematicsDisplay(student.nextLevelMaterials),
        '教材変更 – 変更後教材（悟）': basicTeachingMaterialAdvancedMathematicsDisplay(student.nextLevelMaterials),
        利用開始月予約: preRegistrationDateStatusDisplay(student),
        休会開始月: basicJapaneseYearMonth(student.deactivatedDatetime),
        ステータス: preRegistrationStatusDisplay(student),
        体験ID: basicPresent(student.uid),
        体験ID名: basicTrialStudentNameIdPresent(student),
        体験コード: basicPresent(student.trialCourseCode),
        ステータス更新日: basicPresent(student.statusUpdateDate),
      };

      return pick(data, fields);
    });
  },
  existsRegistrationRequiredParams: (state) => {
    if (!state.editingStudent.uid) return false;
    if (!state.editingStudent.password) return false;
    if (!state.editingStudent.name) return false;
    if (!state.editingStudent.nameKana) return false;
    if (!state.editingStudent.nameRomaji) return false;
    if (!state.editingStudent.address) return false;
    if (!state.editingStudent.figuredTeachingMaterialCodes.length > 0) return false;

    return true;
  },
  existsUpdateRequiredParams: (state) => {
    if (!state.editingStudent.name) return false;
    if (!state.editingStudent.nameKana) return false;
    if (!state.editingStudent.nameRomaji) return false;
    if (!state.editingStudent.address) return false;

    return true;
  },
};

const formatStudents = (students) => {
  students.forEach((student) => {
    formatStudent(student);
  });
  return students;
};

const formatStudent = (student) => {
  if (!isEmpty(student.figuredTeachingMaterials)) {
    let figuredTeachingMaterialCodes = map(student.figuredTeachingMaterials, (material) => material.code);
    figuredTeachingMaterialCodes = sortBy(figuredTeachingMaterialCodes, (code) => code);
    student.figuredTeachingMaterialCodes = figuredTeachingMaterialCodes;
  }

  if (!isEmpty(student.nextFiguredTeachingMaterials)) {
    let nextFiguredTeachingMaterialCodes = map(student.nextFiguredTeachingMaterials, (material) => material.code);
    nextFiguredTeachingMaterialCodes = sortBy(nextFiguredTeachingMaterialCodes, (code) => code);
    student.nextFiguredTeachingMaterialCodes = nextFiguredTeachingMaterialCodes;
    student.prevNextFiguredTeachingMaterialCodes = nextFiguredTeachingMaterialCodes;
  }

  if (isEmpty(student.gender)) student.gender = {};
  if (isEmpty(student.citizenship)) student.citizenship = {};
  if (isEmpty(student.residence)) student.residence = {};
  if (isEmpty(student.schoolYear)) student.schoolYear = {};
};
